import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { Breadcrumb, Prose, ReviewStamp } from "@/components/site/ContentPrimitives";
import { Link } from "react-router-dom";
import {
  useSEO,
  useJsonLd,
  SITE_URL,
  breadcrumbSchema,
} from "@/lib/seo";

const TERMS = [
  {
    term: "Disposable package tracker",
    definition:
      "A single-use tracking device applied directly to a parcel, carton, or pallet, used for one shipment and not recovered, recharged, or returned. Also called a disposable smart label or single-use tracker.",
    link: "/disposable-package-tracker",
  },
  {
    term: "Disposable smart label",
    definition:
      "A thin, adhesive-backed disposable package tracker. Most disposable smart labels use Bluetooth Low Energy (BLE); some use cellular / GNSS. Sold as a peel-and-stick label rather than a housed device.",
    link: "/disposable-smart-labels",
  },
  {
    term: "Disposable GPS tracker",
    definition:
      "Buyer vocabulary for a single-use tracker. Genuine GPS-grade positioning requires a GNSS receiver, which is present in cellular / GNSS disposable devices but not in ultra-thin BLE smart labels.",
    link: "/disposable-gps-tracker",
  },
  {
    term: "Single-use tracker",
    definition:
      "The neutral, architecture-agnostic term for a tracking device designed for one shipment. Covers both BLE smart labels and cellular / GNSS disposable devices.",
  },
  {
    term: "GPS tracking sticker",
    definition:
      "Language borrowed from the peel-and-stick form factor of disposable trackers. Only accurate for products that include a GNSS receiver; BLE smart labels are stickers but not GPS.",
  },
  {
    term: "Package tracker",
    definition:
      "Any device that provides location — and sometimes condition — data for a package during transit. Can be disposable or reusable.",
    link: "/gps-tracker-for-packages",
  },
  {
    term: "Shipment tracker",
    definition:
      "Umbrella term for any tracking device attached to a shipment (parcel, pallet, container). Reusable trackers, disposable smart labels, and RFID / NFC tags are all shipment trackers in this broad sense.",
  },
  {
    term: "BLE smart label",
    definition:
      "A disposable smart label whose primary connectivity is Bluetooth Low Energy. Position is derived from BLE gateway detections; on-board condition sensors are rare in the ultra-thin class.",
    link: "/bluetooth-smart-labels",
  },
  {
    term: "Cellular smart label",
    definition:
      "A disposable smart label that reports directly over a cellular network (typically 4G LTE with 2G fallback), usually paired with GNSS for position and additional condition sensors.",
  },
  {
    term: "GNSS",
    definition:
      "Global Navigation Satellite System — the family of satellite constellations that provide position, including GPS (USA), GLONASS (Russia), BeiDou / BDS (China), Galileo (EU), and QZSS (Japan). Cellular disposable devices commonly combine two or more constellations.",
  },
  {
    term: "GPS",
    definition:
      "The United States government's satellite navigation constellation. Popularly used as a synonym for GNSS, though technically only one constellation among several.",
  },
  {
    term: "Wi-Fi positioning",
    definition:
      "A position-assist technique that infers a device's location from surrounding Wi-Fi access points. Used to accelerate first-fix on cellular / GNSS devices and to add urban indoor context.",
  },
  {
    term: "Geofence",
    definition:
      "A virtual perimeter drawn around a real-world location. Trackers configured with geofences produce entry / exit events which trigger platform alerts and workflow.",
  },
  {
    term: "Dwell",
    definition:
      "Time a shipment spends stationary at a location or node. High dwell can indicate congestion, hand-off delay, or an exception worth investigating.",
  },
  {
    term: "Reporting interval",
    definition:
      "The configured frequency at which a tracker samples position and sensors and transmits data. Faster intervals give higher resolution but consume more battery.",
  },
  {
    term: "Reverse logistics",
    definition:
      "The processes and flows required to return goods — including tracking hardware — after a shipment. A disposable tracker programme has minimal reverse logistics by design.",
  },
  {
    term: "Condition monitoring",
    definition:
      "Continuous or periodic measurement of shipment conditions — temperature, humidity, shock, vibration, ambient light, opening. Requires devices with the corresponding sensors.",
  },
  {
    term: "Temperature excursion",
    definition:
      "An event where a shipment's measured temperature moves outside a defined acceptable range — critical for pharmaceutical and cold-chain lanes.",
  },
  {
    term: "Shock event",
    definition:
      "An accelerometer-detected impact that exceeds a configured threshold. Used to flag potentially damaged high-value shipments for inspection on arrival.",
  },
  {
    term: "Light sensor",
    definition:
      "An ambient-light sensor that can detect a package being opened or a container seal being broken between waypoints.",
  },
  {
    term: "eSIM",
    definition:
      "Embedded SIM. A soldered SIM that provisions cellular connectivity in software, without a physical SIM card. Enables global connectivity from a single device SKU.",
  },
];

export default function Glossary() {
  useSEO({
    title: "Disposable Package Tracker Glossary | Category Definitions",
    description:
      "Concise, technically accurate definitions for disposable package trackers, smart labels, BLE, cellular, GNSS, condition sensors, and related terminology.",
    canonical: "/glossary",
    image: `${SITE_URL}/images/ll100-hero.jpg`,
  });

  useJsonLd(
    "breadcrumb-glossary",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Glossary" },
    ]),
  );

  useJsonLd("defined-term-set", {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Disposable Package Tracker Glossary",
    hasDefinedTerm: TERMS.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${SITE_URL}/glossary`,
      ...(t.link ? { url: `${SITE_URL}${t.link}` } : {}),
    })),
  });

  return (
    <div>
      <PageHero
        eyebrow="Reference"
        title="Glossary — Disposable Package Tracker Terminology"
        subtitle="Concise, technically accurate definitions for the terminology used across disposable package tracking — devices, technologies, sensors, and operational concepts."
      />

      <section className="section">
        <div className="container-x">
          <Breadcrumb
            trail={[
              { label: "Home", href: "/" },
              { label: "Glossary" },
            ]}
          />

          <Prose>
            <div className="mt-6 space-y-8">
              {TERMS.map(({ term, definition, link }) => (
                <article
                  key={term}
                  className="border-l-2 border-line hover:border-brand transition-colors pl-6"
                  data-testid={`glossary-${term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <h2 className="font-heading text-lg font-semibold text-ink">
                    {term}
                  </h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink-muted">
                    {definition}
                  </p>
                  {link && (
                    <Link
                      to={link}
                      className="mt-3 inline-block text-xs font-mono uppercase tracking-widest text-brand hover:underline"
                    >
                      Read more →
                    </Link>
                  )}
                </article>
              ))}
            </div>

            <ReviewStamp
              date="February 2026"
              basis="Manufacturer specifications, direct deployment experience, and referenced schema.org / industry documentation."
            />
          </Prose>
        </div>
      </section>

      <RelatedLinks
        title="Related reading"
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
          { to: "/how-disposable-trackers-work", label: "How Disposable Trackers Work — end-to-end lifecycle" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable — full comparison" },
        ]}
      />

      <CTAStrip
        title="Missing a term?"
        body="Tell us which one — we'll add it. The glossary is meant to grow with the category, not be locked."
      />
    </div>
  );
}
